import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";
import {
  FiEye,
  FiEyeOff,
  FiRefreshCw,
  FiMail,
  FiLock,
  FiShield,
} from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import ThemeToggle from "../../Components/ThemeToggle";
import coinSwitchPay from "../../assets/coinSwitchPay.png";
import { makeRequest } from "../../_config/api";

export default function Login() {
  const [admin, setAdmin] = useState({ userName: "", password: "", otp: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isOtpSent, setIsOtpSent] = useState(false); // OTP state
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAdmin((prev) => ({ ...prev, [name]: value }));
  };

  // Step 1: Request OTP or Initial Login Check
  const handleInitialSubmit = async (e) => {
    e.preventDefault();

    if (!admin.userName || !admin.password) {
      showErrorToast("Username and Password are required");
      return;
    }

    setLoading(true);

    try {
      const { data, error, message } = await makeRequest(
        "post",
        "/admin/adminLogin",
        {
          email: admin.userName,
          password: admin.password,
        },
      );

      if (error) {
        showErrorToast(message || "Login failed");
        return;
      }

      // ✅ IMPORTANT: token save karo
      localStorage.setItem("loginToken", data.token);

      setIsOtpSent(true);
      showSuccessToast("OTP sent successfully");
    } catch (error) {
      showErrorToast("Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Final Verification & Login
  const handleFinalSubmit = async (e) => {
    e.preventDefault();

    if (!admin.otp) {
      showErrorToast("Please enter OTP");
      return;
    }

    setLoading(true);

    try {
      const loginToken = localStorage.getItem("loginToken");

      const { data, error, message } = await makeRequest(
        "post",
        "/admin/verifyAdmin",
        {
          token: loginToken,
          otp: admin.otp,
        },
      );

      if (error) {
        showErrorToast(message || "Invalid OTP");
        return;
      }

      showSuccessToast("Login Successful!");

      // ✅ final JWT store
      localStorage.setItem(
        "user",
        JSON.stringify({ token: data.token, userId: data.id }),
      );

      // optional cleanup
      localStorage.removeItem("loginToken");

      setTimeout(() => navigate("/"), 800);
    } catch (error) {
      showErrorToast("Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-gray-950 p-4 transition-colors duration-300">
      <ToastContainer theme="colored" />

      {/* Theme Toggle Floating */}
      <div className="absolute top-5 right-5 z-50">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-900 shadow-2xl rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800">
          <div className="p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src={coinSwitchPay}
                alt="Logo"
                className="h-12 w-auto object-contain dark:invert"
              />
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                {isOtpSent ? "Verify OTP" : "Admin Login"}
              </h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">
                {isOtpSent
                  ? `Enter the code sent to ${admin.userName}`
                  : "Secure access to your dashboard"}
              </p>
            </div>

            <form
              onSubmit={isOtpSent ? handleFinalSubmit : handleInitialSubmit}
              className="space-y-5"
            >
              {!isOtpSent ? (
                <>
                  {/* Email/User Field */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">
                      Username
                    </label>
                    <div className="relative">
                      <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        name="userName"
                        value={admin.userName}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                        placeholder="admin@example.com"
                      />
                    </div>
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">
                      Password
                    </label>
                    <div className="relative">
                      <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={admin.password}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-12 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all dark:text-white"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-500"
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                /* OTP Field */
                <div className="space-y-2 animate-in fade-in slide-in-from-bottom-4">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">
                    Verification Code
                  </label>
                  <div className="relative">
                    <FiShield className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="otp"
                      maxLength="6"
                      value={admin.otp}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-green-500 outline-none transition-all dark:text-white tracking-[0.5em] font-bold text-center"
                      placeholder="000000"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsOtpSent(false)}
                    className="text-xs text-blue-600 hover:underline mt-2 inline-block"
                  >
                    Edit Login Details
                  </button>
                </div>
              )}

              {/* Action Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-4 bg-gray-900 dark:bg-blue-600 hover:bg-black dark:hover:bg-blue-700 text-white font-semibold rounded-xl transition-all shadow-lg active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <FiRefreshCw className="animate-spin" />
                ) : isOtpSent ? (
                  "Verify & Enter"
                ) : (
                  "Get Security Code"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center mt-8 text-gray-500 dark:text-gray-500 text-xs">
          © {new Date().getFullYear()} Coin Switch Pay. Secure Infrastructure.
        </p>
      </div>
    </div>
  );
}
